// src/pages/DriverDashboard.jsx
import { useEffect, useMemo, useState } from "react";
import WeeklyCalendar from "./WeeklyCalendar";
import TripMapViewer from "./TripMapViewer";
import { ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsCarBooking";
import axios from "axios";
import AlertDialog from "../../Lms/Components/Shared/AlertDialog";
import './../../../style/Vars.css';
import './../../../style/ElementsStyle.css';
import './../../../style/ContainersStyles.css';
import "../Styles/Drivers.css";

import {
  getSessionUserId,
  getSessionUserName,
} from "../../../utils/SessionUser";

const DRIVER_COLOR = "var(--gradient-driver-main)";

// Helpers básicos
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay(); // 0=Dom, 1=Lun, ..., 6=Sab
  const diff = (day === 0 ? -6 : 1) - day; // mover al lunes
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateTimeRange(start, end) {
  if (!(start instanceof Date) || !(end instanceof Date)) return "";
  const sameDate = start.toDateString() === end.toDateString();
  const startDate = start.toLocaleDateString("es-MX");
  const endDate = end.toLocaleDateString("es-MX");
  const startTime = start.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  const endTime = end.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

  if (sameDate) {
    return `${startDate} · ${startTime} - ${endTime}`;
  }

  return `${startDate} · ${startTime} → ${endDate} · ${endTime}`;
}

function toDateWithTime(dateStr, timeStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh = 0, mm = 0] = (timeStr || "00:00").split(":").map(Number);
  return new Date(y - 0, m - 1, d, hh, mm, 0, 0);
}

function setTimeOnDate(date, timeStr) {
  const [hh = 0, mm = 0] = (timeStr || "00:00").split(":").map(Number);
  const d = new Date(date);
  d.setHours(hh, mm, 0, 0);
  return d;
}

// ===== Helpers para horas extra =====
function toMinutes(hm) {
  const [h, m] = (hm || "0:0").split(":").map(Number);
  return h * 60 + m;
}

function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

// Festivos fijos (ajusta según tus reglas)
const FESTIVOS_FIJOS = ["2025-01-01"];

function isHoliday(date) {
  const ymd = formatDateLocal(date);
  return FESTIVOS_FIJOS.includes(ymd);
}

function getISOWeek(date) {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// --- Helpers para Excepciones de turno (mismos que en ReservationPage) ---
function normalizeDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date) {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const s = String(value);
  const parts = s.slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

/**
 * A partir de:
 *  - fecha (Date)
 *  - idChofer
 *  - baseIntervals: [{ start: "HH:mm", end: "HH:mm" }] desde turnos
 *  - excepcionesTurno: arreglo crudo del backend
 *
 * Devuelve los intervalos REALES del día:
 *  - Si hay excepciones activas para ese chofer y día → solo esas.
 *  - Si no hay → los intervalos base.
 */
function buildDailyIntervalsForDriver(date, driverId, baseIntervals, excepciones) {
  if (!driverId || !date) return baseIntervals || [];

  const target = normalizeDateOnly(date);
  if (!target) return baseIntervals || [];

  const relevant = (excepciones || []).filter((ex) => {
    const exChofer =
      ex.idChofer ??
      ex.IdChofer ??
      ex["idChofer"] ??
      ex["IdChofer"] ??
      null;
    if (Number(exChofer) !== Number(driverId)) return false;
    if (ex.activo === false || ex.Activo === false) return false;

    const fi = normalizeDateOnly(ex.fechaInicio ?? ex.FechaInicio);
    const ff = normalizeDateOnly(
      ex.fechaFin ?? ex.FechaFin ?? ex.fechaInicio ?? ex.FechaInicio
    );
    if (!fi) return false;

    const end = ff || fi;
    const t = target.getTime();
    return t >= fi.getTime() && t <= end.getTime();
  });

  // Sin excepciones → horario base
  if (relevant.length === 0) return baseIntervals || [];

  // Hay una o varias excepciones → el día se rige SOLO por estas
  const exIntervals = relevant
    .map((ex) => {
      const hIni = String((ex.horaInicio ?? ex.HoraInicio) || "").slice(0, 5);
      const hFin = String((ex.horaFin ?? ex.HoraFin) || "").slice(0, 5);
      if (!hIni || !hFin) return null;
      if (toMinutes(hFin) <= toMinutes(hIni)) return null;
      return { start: hIni, end: hFin };
    })
    .filter(Boolean);

  // Si por alguna razón no quedó ningún intervalo válido, devolvemos base
  return exIntervals.length > 0 ? exIntervals : baseIntervals || [];
}

// --- Helpers para mapear reservas del backend a eventos de calendario ---

function parseJsonLocation(value) {
  if (!value) return { address: "", location: null };
  try {
    const v = typeof value === "string" ? JSON.parse(value) : value;
    if (v && typeof v === "object") {
      return {
        address: v.address || v.direccion || "",
        location: v.location || null,
      };
    }
  } catch {
    return { address: String(value || ""), location: null };
  }
  return { address: "", location: null };
}

/**
 * NO TOCA el DTO:
 * - r viene ya normalizado por Models.reserva
 * - meta contiene el DTO tal cual
 * - Solo agregamos campos extra: originParsed, destinationParsed, estadoNombre
 */
function mapReservaToEvent(r) {
  // DTO tal cual
  const dto = {
    idReserva: r.idReserva,
    idSolicitante: r.idSolicitante,
    idChofer: r.idChofer,
    idVehiculo: r.idVehiculo,
    fechaInicio: r.fechaInicio,
    fechaFin: r.fechaFin,
    horaInicio: r.horaInicio,
    horaFin: r.horaFin,
    origenTexto: r.origenTexto,
    destinoTexto: r.destinoTexto,
    motivo: r.motivo,
    idEstadoReserva: r.idEstadoReserva,
    fueraHorarioMin: r.fueraHorarioMin,
    observaciones: r.observaciones,
    fechaCreacion: r.fechaCreacion,
    activo: r.activo,

    // para saber si tuvo flujo de aprobación
    idTipoAprobacion: r.idTipoAprobacion,
  };

  const id = dto.idReserva ?? 0;

  // 1) Normalizar fecha (YYYY-MM-DD) usando fechaInicio
  const normalizeDate = (val) => {
    if (!val) return formatDateLocal(new Date());
    if (val instanceof Date) return formatDateLocal(val);
    const s = String(val);
    return s.slice(0, 10);
  };

  const rawFechaInicio = dto.fechaInicio || new Date().toISOString();
  const rawFechaFin = dto.fechaFin || rawFechaInicio;

  const fecha = normalizeDate(rawFechaInicio);
  const fechaFin = normalizeDate(rawFechaFin);

  // 2) Normalizar horas HH:mm
  const normalizeHour = (val) => {
    if (!val) return "00:00";
    const s = String(val);
    return s.slice(0, 5);
  };

  const horaInicio = normalizeHour(dto.horaInicio);
  const horaFin = normalizeHour(dto.horaFin);

  // 3) Construir fechas completas
  const start = toDateWithTime(fecha, horaInicio);
  const end = toDateWithTime(fechaFin, horaFin);

  // Extras de UI a partir de origenTexto/destinoTexto
  const originParsed = parseJsonLocation(dto.origenTexto);
  const destinationParsed = parseJsonLocation(dto.destinoTexto);

  // Nombre “bonito” de estado (nuevos estados 1–6)
  let estadoNombre = null;
  switch (dto.idEstadoReserva) {
    case 1:
      estadoNombre = "Pendiente de autorización";
      break;
    case 2:
      estadoNombre = "Confirmada";
      break;
    case 3:
      estadoNombre = "Autorizada";
      break;
    case 4:
      estadoNombre = "Rechazada";
      break;
    case 5:
      estadoNombre = "Cancelada";
      break;
    case 6:
      estadoNombre = "Completada";
      break;
    default:
      estadoNombre = null;
  }

  const meta = {
    ...dto,
    originParsed,
    destinationParsed,
    estadoNombre,
  };

  return {
    id,
    start,
    end,
    title: dto.motivo || "Reserva",
    meta,
  };
}

const endpointReservas = ENDPOINTS.reserva;
const modeloReservas = Models.reserva;

const endpointChoferes = ENDPOINTS.chofer;
const modeloChofer = Models.chofer;
const endpointTurnos = ENDPOINTS.turnos;
const modeloTurnos = Models.turnos;
const endpointEmpleadoTurno = ENDPOINTS.empleadosTurnos;
const modeloEmpleadoTurnos = Models.empleadosTurnos;

// 🚗 Vehículos y asignaciones chofer–vehículo
const endpointVehiculos = ENDPOINTS.vehiculo;
const modeloVehiculo = Models.vehiculo;
const endpointChoferVehiculo = ENDPOINTS.choferVehiculoAsignacion;
const modeloChoferVehiculo = Models.choferVehiculoAsignacion;

// Checklist viaje
const endpointChecklistViaje = ENDPOINTS.checklistViaje;

// Endpoints reales para combustible y siniestros
const endpointCargasCombustible = ENDPOINTS.vehiculoCombustible;
const endpointSiniestros = ENDPOINTS.siniestro;

// 🔹 Aprobaciones de reservas (comentario RH)
const endpointAprobaciones = ENDPOINTS.aprobacionesReservas;
const modeloAprobaciones = Models.aprobacionesReservas;

// 🔹 Para ligar usuario → persona → empleado → chofer
const endpointUsuarios = ENDPOINTS.usuarios;
const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;
const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

// 🔹 Extras acumuladas (mes / semana) — sólo INSERT
const endpointExtrasMes = ENDPOINTS.extrasAcumuladoMes;
const endpointExtrasSemana = ENDPOINTS.extrasAcumuladoSemana;

// 🔹 Excepciones de turno
const endpointExcepcionTurno = ENDPOINTS.excepcionTurno;
const modeloExcepcionTurno = Models.excepcionTurno;

const endpointSolicitantes = ENDPOINTS.solicitantes;
const modeloSolicitantes = Models.solicitantes;

const eventGradientMap = {
  pending: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
  completed: "linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)",
  default: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
};

export function getCalendarEventGradient(meta = {}) {
  const estadoKey = (meta.estadoKey || meta.idEstadoReserva || "").toString().toUpperCase();
  const tripStatus = (meta.viajeStatus || "").toString().toUpperCase();

  if (tripStatus === "COMPLETADO") return eventGradientMap.completed;
  if (estadoKey === "PENDING" || estadoKey === "1" || estadoKey === "PENDIENTE") {
    return eventGradientMap.pending;
  }
  return eventGradientMap.default;
}

export default function DriverDashboard() {
  const [anchor, setAnchor] = useState(new Date());
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);

  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: 'Información',
    message: '',
    variant: 'info'
  });

  const [vehicles, setVehicles] = useState([]);
  const [preferredVehicleIds, setPreferredVehicleIds] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");

  const [showStartModal, setShowStartModal] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);

  const [startForm, setStartForm] = useState({
    combustibleInicial: "",
    kmInicial: "",
    observacionesInicial: "",
  });

  const [finishForm, setFinishForm] = useState({
    combustibleFinal: "",
    kmFinal: "",
    observacionesFinal: "",
    huboSiniestro: false,
    tipoSiniestro: "",
    importeSiniestro: "",
    descripcionSiniestro: "",
    recargoCombustible: false,
    litros: "",
    importeCombustible: "",
    estacion: "",
    notasCombustible: "",
  });

  const driverColor = useMemo(() => DRIVER_COLOR, []);

  // ====== Datos de horarios desde BD ======
  const [choferes, setChoferes] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [empleadosTurnos, setEmpleadosTurnos] = useState([]);
  const [driverIntervals, setDriverIntervals] = useState([]);

  // 🔹 Excepciones de turno
  const [excepcionesTurno, setExcepcionesTurno] = useState([]);

  // 🚗 Vehículos y asignaciones desde BD
  const [allVehiculos, setAllVehiculos] = useState([]);
  const [asignacionesVehiculo, setAsignacionesVehiculo] = useState([]);
  // 🔹 Aprobaciones por reserva (idReserva → última aprobación activa)
  const [aprobacionesPorReserva, setAprobacionesPorReserva] = useState({});

  // 🔹 Usuario en sesión y chofer asociado
  const [sessionUserId] = useState(() => getSessionUserId());
  const [sessionUserName] = useState(() => getSessionUserName());
  const [sessionChoferId, setSessionChoferId] = useState(null);
  const [driverName, setDriverName] = useState(sessionUserName || "Chofer");

  const choferUoMap = useMemo(() => {
    if (!choferes.length || !empleadosTurnos.length) {
      return new Map();
    }

    const map = new Map();
    choferes.forEach((ch) => {
      if (!ch.idChofer || !ch.idEmpleado) return;
      const uoSet = new Set(
        empleadosTurnos
          .filter((rel) => {
            const empId = rel.idEmpleado ?? rel.IdEmpleado ?? rel["idEmpleado"] ?? rel["IdEmpleado"] ?? null;
            const activo = rel.activo ?? rel.Activo ?? true;
            return activo && Number(empId) === Number(ch.idEmpleado);
          })
          .map((rel) => {
            const uoId =
              rel.idUnidadOrganizacional ??
              rel.IdUnidadOrganizacional ??
              rel["idUnidadOrganizacional"] ??
              rel["IdUnidadOrganizacional"] ??
              null;
            return Number(uoId) || null;
          })
          .filter(Boolean)
      );
      map.set(ch.idChofer, uoSet);
    });

    return map;
  }, [choferes, empleadosTurnos]);

  const sessionChoferUoSet = useMemo(() => {
    if (!sessionChoferId) return new Set();
    return new Set(choferUoMap.get(sessionChoferId) || []);
  }, [choferUoMap, sessionChoferId]);

  // 🔒 Errores críticos de sesión (bloquean toda la ventana)
  const [sessionErrors, setSessionErrors] = useState([]);
  const hasSessionErrors = sessionErrors.length > 0;

  const loadChoferes = async () => {
    try {
      const res = await axios.get(endpointChoferes);
      setChoferes((res.data || []).map(modeloChofer));
    } catch (_) {
      console.error("Error al cargar choferes");
    }
  };

  const loadTurnos = async () => {
    try {
      const res = await axios.get(endpointTurnos);
      setTurnos((res.data || []).map(modeloTurnos));
    } catch (_) {
      console.error("Error al cargar turnos");
    }
  };

  const loadEmpleadosTurnos = async () => {
    try {
      const res = await axios.get(endpointEmpleadoTurno);
      setEmpleadosTurnos((res.data || []).map(modeloEmpleadoTurnos));
    } catch (_) {
      console.error("Error al cargar empleados-turnos");
    }
  };

  const loadVehiculos = async () => {
    try {
      const res = await axios.get(endpointVehiculos);
      setAllVehiculos((res.data || []).map(modeloVehiculo));
    } catch (err) {
      console.error("Error al cargar vehículos", err);
    }
  };

  const loadChoferVehiculoAsignaciones = async () => {
    try {
      const res = await axios.get(endpointChoferVehiculo);
      setAsignacionesVehiculo((res.data || []).map(modeloChoferVehiculo));
    } catch (err) {
      console.error("Error al cargar asignaciones chofer–vehículo", err);
    }
  };

  // 🔹 Cargar aprobaciones (comentarios de RH)
  const loadAprobaciones = async () => {
    try {
      const res = await axios.get(endpointAprobaciones);
      const rows = (res.data || []).map(modeloAprobaciones);

      const activas = rows.filter((a) => a.activo ?? true);

      const map = {};
      for (const a of activas) {
        const idRes = Number(a.idReserva ?? 0);
        if (!idRes) continue;

        const current = map[idRes];
        if (!current) {
          map[idRes] = a;
        } else {
          const faNew = a.fechaAprobacion ? new Date(a.fechaAprobacion) : null;
          const faCur = current.fechaAprobacion
            ? new Date(current.fechaAprobacion)
            : null;

          let useNew = false;
          if (faNew && faCur) {
            useNew = faNew > faCur;
          } else if (faNew && !faCur) {
            useNew = true;
          } else if (!faNew && !faCur) {
            const idNew = Number(a.idAprovacionesReservas ?? 0);
            const idCur = Number(current.idAprovacionesReservas ?? 0);
            if (idNew > idCur) useNew = true;
          }

          if (useNew) {
            map[idRes] = a;
          }
        }
      }

      setAprobacionesPorReserva(map);
    } catch (err) {
      console.error("Error al cargar aprobaciones de reservas", err);
    }
  };

  // 🔹 Cargar excepciones de turno
  const loadExcepcionesTurno = async () => {
    try {
      const res = await axios.get(endpointExcepcionTurno);
      setExcepcionesTurno((res.data || []).map(modeloExcepcionTurno));
    } catch (err) {
      console.error("Error al cargar excepciones de turno", err);
    }
  };

  // Carga inicial "estática"
  useEffect(() => {
    loadVehiculos();
    loadChoferVehiculoAsignaciones();
    loadChoferes();
    loadTurnos();
    loadEmpleadosTurnos();
    loadAprobaciones();
    loadExcepcionesTurno();
  }, []);

  // 🔹 Ligar usuario en sesión → chofer (idChofer) y nombre del chofer
  useEffect(() => {
    let cancel = false;

    async function linkChoferFromSession() {
      const errors = [];

      try {
        // 0) idUsuario en sesión
        if (!sessionUserId) {
          const msg =
            "[DriverDashboard] No hay idUsuario en sesión; no se puede operar este panel.";
          console.warn(msg);
          errors.push(
            "No se detecta un usuario en sesión. No es posible operar el panel de chofer."
          );
        } else {
          // Esperamos a tener choferes cargados (no es un error de usuario)
          if (!choferes.length) {
            // Se reintentará cuando choferes cambie
            return;
          }

          // 1) Usuarios: obtener TODOS y filtrar por idUsuario
          const resUser = await axios.get(endpointUsuarios);
          const usuariosRaw = resUser.data || [];

          const rawUser =
            usuariosRaw.find((u) => {
              const id =
                u.idUsuario ??
                u.IdUsuario ??
                u["idUsuario"] ??
                u["IdUsuario"];
              return String(id) === String(sessionUserId);
            }) || null;

          if (!rawUser) {
            const msg =
              "[DriverDashboard] No se encontró usuario de sesión en el catálogo de usuarios.";
            console.warn(msg);
            errors.push(
              "El usuario de sesión no está registrado en el catálogo de usuarios."
            );
          }

          // 2) IdPersona
          const idPersona =
            rawUser?.idPersona ??
            rawUser?.IdPersona ??
            rawUser?.["idPersona"] ??
            rawUser?.["IdPersona"] ??
            null;

          if (!idPersona) {
            const msg =
              "[DriverDashboard] El usuario de sesión no tiene IdPersona asociado.";
            console.warn(msg, rawUser);
            errors.push(
              "El usuario de sesión no está vinculado a una Persona en el sistema."
            );
          } else {
            // 3) Empleados: buscar el empleado que tenga ese idPersona
            const resEmp = await axios.get(endpointEmpleado);
            const empleados = (resEmp.data || []).map(modeloEmpleado);

            const empSesion = empleados.find(
              (e) => e.idPersona === idPersona
            );

            if (!empSesion) {
              const msg =
                "[DriverDashboard] No se encontró empleado asociado a la Persona del usuario de sesión.";
              console.warn(msg);
              errors.push(
                "El usuario de sesión no está registrado como Empleado."
              );
            } else {
              // 4) Chofer: buscar el chofer que tenga idEmpleado = empSesion.idEmpleado
              const chSesion = choferes.find(
                (c) => c.idEmpleado === empSesion.idEmpleado
              );

              if (!chSesion) {
                const msg =
                  "[DriverDashboard] No se encontró registro de chofer asociado al empleado del usuario de sesión.";
                console.warn(msg);
                errors.push(
                  "El usuario de sesión no está registrado como Chofer."
                );
              } else {
                // 5) Persona (para el nombre completo)
                const resPer = await axios.get(endpointPersona);
                const personas = (resPer.data || []).map(modeloPersona);
                const personaSesion = personas.find(
                  (p) => p.idPersona === idPersona
                );

                if (cancel) return;

                // Si no hubo errores hasta aquí, ligamos chofer + nombre
                if (errors.length === 0) {
                  setSessionChoferId(chSesion.idChofer);

                  const nombreCompleto = personaSesion
                    ? `${personaSesion.nombre || ""} ${personaSesion.apellidoPaterno || ""
                      } ${personaSesion.apellidoMaterno || ""}`.trim()
                    : sessionUserName || "Chofer";

                  setDriverName(nombreCompleto || "Chofer");
                }
              }
            }
          }
        }
      } catch (err) {
        console.error(
          "[DriverDashboard] Error ligando usuario de sesión a chofer:",
          err
        );
        errors.push(
          "Ocurrió un error al intentar resolver la información del chofer para la sesión actual."
        );
      }

      if (cancel) return;

      if (errors.length > 0) {
        setSessionErrors(errors);
        setSessionChoferId(null);
        // errors.forEach((msg) => alert(msg));
      } else {
        setSessionErrors([]);
      }
    }

    linkChoferFromSession();

    return () => {
      cancel = true;
    };
  }, [sessionUserId, sessionUserName, choferes]);

  // Recalcular driverIntervals para el chofer de sesión (turno base)
  useEffect(() => {
    if (
      !sessionChoferId ||
      !choferes.length ||
      !turnos.length ||
      !empleadosTurnos.length
    ) {
      setDriverIntervals([]);
      return;
    }

    const ch = choferes.find((c) => c.idChofer === sessionChoferId);
    if (!ch || !ch.idEmpleado) {
      setDriverIntervals([]);
      return;
    }

    const asignaciones = empleadosTurnos.filter(
      (et) => et.idEmpleado === ch.idEmpleado
    );
    if (!asignaciones.length) {
      setDriverIntervals([]);
      return;
    }

    const turnoIds = [
      ...new Set(asignaciones.map((a) => a.idTurno).filter(Boolean)),
    ];
    if (!turnoIds.length) {
      setDriverIntervals([]);
      return;
    }

    const intervals = turnoIds
      .map((idT) => turnos.find((t) => t.idTurno === idT))
      .filter(Boolean)
      .map((t) => ({
        start: String(t.horaInicio || "").slice(0, 5),
        end: String(t.horaFin || "").slice(0, 5),
      }));

    setDriverIntervals(intervals);
  }, [sessionChoferId, choferes, turnos, empleadosTurnos]);

  // 🚗 Filtrar vehículos asignados al chofer de sesión
  useEffect(() => {
    if (!allVehiculos.length) {
      setVehicles([]);
      setPreferredVehicleIds([]);
      return;
    }

    const activeVehicles = allVehiculos.filter(
      (v) => v.activo === undefined || v.activo === true
    );

    if (!asignacionesVehiculo.length) {
      setVehicles(activeVehicles);
      setPreferredVehicleIds([]);
      return;
    }

    const preferredIds = new Set();
    const sessionUoSet = sessionChoferUoSet;
    const includeAll = sessionUoSet.size === 0;

    const allowedVehicleIds = new Set();

    asignacionesVehiculo.forEach((asign) => {
      if (!asign.activo) return;
      const vehId = asign.idVehiculo;
      if (!vehId) return;

      if (asign.idChofer === sessionChoferId) {
        preferredIds.add(vehId);
        allowedVehicleIds.add(vehId);
        return;
      }

      if (!sessionChoferId) return;

      const choferUos = choferUoMap.get(asign.idChofer) || new Set();
      if (includeAll) {
        allowedVehicleIds.add(vehId);
        return;
      }

      const overlaps = [...choferUos].some((uoId) => sessionUoSet.has(Number(uoId)));
      if (overlaps) {
        allowedVehicleIds.add(vehId);
      }
    });

    const filteredVehicles = activeVehicles.filter((v) => allowedVehicleIds.has(v.idVehiculo));
    setVehicles(filteredVehicles);
    setPreferredVehicleIds(Array.from(preferredIds));
  }, [
    sessionChoferId,
    allVehiculos,
    asignacionesVehiculo,
    choferUoMap,
    sessionChoferUoSet,
  ]);

  const preferredVehicleSet = useMemo(
    () => new Set(preferredVehicleIds.map(Number)),
    [preferredVehicleIds]
  );

  const sortedVehicles = useMemo(() => {
    if (!vehicles.length) return [];
    return [...vehicles].sort((a, b) => {
      const aPreferred = preferredVehicleSet.has(Number(a.idVehiculo));
      const bPreferred = preferredVehicleSet.has(Number(b.idVehiculo));
      if (aPreferred !== bPreferred) {
        return aPreferred ? -1 : 1;
      }
      const aLabel = `${a.marca || ""} ${a.modelo || ""}`.trim();
      const bLabel = `${b.marca || ""} ${b.modelo || ""}`.trim();
      return aLabel.localeCompare(bLabel);
    });
  }, [vehicles, preferredVehicleSet]);

  // ====== Reservas: leer del backend, filtrar por idChofer de sesión ======
  async function reloadTrips() {
    try {
      if (hasSessionErrors || !sessionChoferId) {
        setTrips([]);
        setSelectedTrip(null);
        setSelectedVehicleId("");
        return;
      }

      const [
        resReservas,
        resSolicitantes,
        resEmpleados,
        resPersonas
      ] = await Promise.all([
        axios.get(endpointReservas),
        axios.get(endpointSolicitantes),
        axios.get(endpointEmpleado),
        axios.get(endpointPersona)
      ]);

      const rows = (resReservas.data || []).map(modeloReservas);
      const solicitantesAll = (resSolicitantes.data || []).map(modeloSolicitantes);
      const empleadosAll = (resEmpleados.data || []).map(modeloEmpleado);
      const personasAll = (resPersonas.data || []).map(modeloPersona);

      const events = rows.map(mapReservaToEvent);

      // Enriquecer con nombre de solicitante (lógica idSolicitante -> idEmpleado -> Persona)
      events.forEach((ev) => {
        if (ev.meta && ev.meta.idSolicitante) {
          const solicitante = solicitantesAll.find(
            (s) => s.idSolicitante === ev.meta.idSolicitante
          );
          if (solicitante && solicitante.idEmpleado) {
            const emp = empleadosAll.find(
              (e) => e.idEmpleado === solicitante.idEmpleado
            );
            if (emp) {
              const per = personasAll.find(
                (p) => p.idPersona === emp.idPersona
              );
              if (per) {
                ev.meta.nombre = `${per.nombre} ${per.apellidoPaterno ?? ""} ${per.apellidoMaterno ?? ""}`.trim();
              }
            }
          }
        }
      });

      // SOLO reservas del chofer en sesión con estado 3 (AUTORIZADA) o 2 (EN CURSO)
      const driverTrips = events.filter((e) => {
        const meta = e.meta || {};
        const metaIdChofer = Number(meta.idChofer ?? 0);
        const estadoId = Number(meta.idEstadoReserva ?? 0);
        return (
          metaIdChofer === Number(sessionChoferId) &&
          (estadoId === 3 || estadoId === 2)
        );
      });

      driverTrips.sort((a, b) => a.start - b.start);

      setTrips(driverTrips);

      if (!driverTrips.length) {
        setSelectedTrip(null);
        setSelectedVehicleId("");
        return;
      }

      setSelectedTrip((prev) => {
        if (!prev) return driverTrips[0];
        const matching = driverTrips.find((t) => t.id === prev.id);
        return matching || driverTrips[0];
      });
    } catch (err) {
      console.error("Error al cargar reservas del chofer", err);
    }
  }

  // carga inicial de trips / y cada vez que cambie el chofer de sesión o haya errores de sesión
  useEffect(() => {
    reloadTrips();
  }, [sessionChoferId, hasSessionErrors]);

  // ====== viajeStatus SOLO en memoria (no BD) ======
  const setTripNewStatus = (idReserva, newStatus, extraMeta = {}) => {
    setTrips((prev) =>
      prev.map((t) => {
        if (t.id !== idReserva) return t;
        const currentStatus = t.meta?.viajeStatus;
        if (newStatus === "EN_CURSO" && currentStatus === "COMPLETADO") {
          return t;
        }
        return {
          ...t,
          meta: {
            ...t.meta,
            viajeStatus: newStatus,
            ...extraMeta,
          },
        };
      })
    );

    setSelectedTrip((prev) => {
      if (!prev || prev.id !== idReserva) return prev;
      const currentStatus = prev.meta?.viajeStatus;
      if (newStatus === "EN_CURSO" && currentStatus === "COMPLETADO") {
        return prev;
      }
      return {
        ...prev,
        meta: {
          ...prev.meta,
          viajeStatus: newStatus,
          ...extraMeta,
        },
      };
    });
  };

  // bloques de disponibilidad (lunes a viernes de la semana del anchor),
  // usando horario REAL por día (turnos + excepciones)
  const availabilityBlocks = useMemo(() => {
    if (!driverIntervals.length || !sessionChoferId) return [];

    const blocks = [];
    const baseMonday = getMonday(anchor);

    for (let i = 0; i < 7; i++) {
      const date = new Date(baseMonday);
      date.setDate(baseMonday.getDate() + i);
      const dow = date.getDay();

      if (dow === 0 || dow === 6) continue;

      // Horarios reales de ese día, aplicando excepciones de turno
      const ivs = buildDailyIntervalsForDriver(
        date,
        sessionChoferId,
        driverIntervals,
        excepcionesTurno
      );

      const dateStr = formatDateLocal(date);

      ivs.forEach((iv, idx) => {
        blocks.push({
          id: `av-${i}-${idx}`,
          start: toDateWithTime(dateStr, iv.start),
          end: toDateWithTime(dateStr, iv.end),
          color: driverColor,
        });
      });
    }

    return blocks;
  }, [anchor, driverIntervals, driverColor, sessionChoferId, excepcionesTurno]);

  const getCurrentVehicle = () => {
    return vehicles.find((v) => String(v.idVehiculo) === selectedVehicleId);
  };

  // Solo mostramos en la UI estados 3 (autorizada) o 2 (en curso); 6 se oculta.
  const tripsForWeek = useMemo(
    () =>
      trips.filter((t) => {
        const est = Number(t.meta?.idEstadoReserva ?? 0);
        return est === 3 || est === 2;
      }),
    [trips]
  );

  // Handlers
  function handleEventClick(evt) {
    if (hasSessionErrors) {
      setAlertDialog({
        open: true,
        title: "Error de Sesión",
        message: "No es posible operar el panel de chofer porque falta información crítica del usuario de sesión.",
        variant: "error"
      });
      return;
    }

    setSelectedTrip(evt);

    const metaVeh = Number(evt.meta?.idVehiculo ?? 0);
    if (metaVeh > 0) {
      setSelectedVehicleId(String(metaVeh));
    }
  }

  // ====== Cálculo de horas extra netas por viaje (usando horario REAL del día) ======
  const computeNetExtraHoursForTrip = (trip) => {
    if (!trip || !driverIntervals.length || !sessionChoferId) return 0;

    const m = trip.meta || {};

    // Fecha base de la reserva
    let fechaStr = "";
    if (typeof m.fechaInicio === "string") {
      fechaStr = String(m.fechaInicio).slice(0, 10);
    } else if (m.fechaInicio instanceof Date) {
      fechaStr = formatDateLocal(m.fechaInicio);
    } else {
      fechaStr = formatDateLocal(trip.start);
    }

    const horaIni =
      (m.horaInicio || trip.start.toTimeString().slice(0, 5)).slice(0, 5);
    const horaFin =
      (m.horaFin || trip.end.toTimeString().slice(0, 5)).slice(0, 5);

    const d = new Date(fechaStr + "T00:00:00");
    if (Number.isNaN(d.getTime())) return 0;

    const startMin = toMinutes(horaIni);
    const endMin = toMinutes(horaFin);
    if (endMin <= startMin) return 0;
    const totalMins = endMin - startMin;

    // Fines de semana / festivos → todo cuenta como extra
    if (isWeekend(d) || isHoliday(d)) {
      return Math.floor(totalMins / 60); // horas completas
    }

    // Horario REAL del día (turno base + excepciones de turno)
    const dailyIntervals = buildDailyIntervalsForDriver(
      d,
      sessionChoferId,
      driverIntervals,
      excepcionesTurno
    );

    // Entre semana → restamos las partes que caen dentro de los intervals reales
    let covered = 0;
    for (const iv of dailyIntervals) {
      const ivStart = toMinutes(iv.start);
      const ivEnd = toMinutes(iv.end);
      const overlapStart = Math.max(startMin, ivStart);
      const overlapEnd = Math.min(endMin, ivEnd);
      if (overlapEnd > overlapStart) {
        covered += overlapEnd - overlapStart;
      }
    }

    const extraMins = Math.max(0, totalMins - covered);
    return Math.floor(extraMins / 60); // horas completas, sin decimales
  };

  // ====== LÓGICA DE BOTONES SEGÚN ESTADO ======
  const estadoReservaId = Number(selectedTrip?.meta?.idEstadoReserva ?? 0);
  const selectedStatus = (
    selectedTrip?.meta?.viajeStatus || "PENDIENTE"
  ).toUpperCase();

  // 👉 Solo se puede INICIAR si está AUTORIZADA (3)
  const canStart = !!selectedTrip && estadoReservaId === 3;
  // 👉 Solo se puede CONCLUIR si está CONFIRMADA/EN CURSO (2)
  const canFinish = !!selectedTrip && estadoReservaId === 2;

  // Comentario de RH
  const selectedRhComment = useMemo(() => {
    if (!selectedTrip) return "";
    const reservaId = Number(
      selectedTrip.meta?.idReserva ?? selectedTrip.id
    );
    if (!reservaId) return "";
    const ap = aprobacionesPorReserva[reservaId];
    return ap?.comentario || "";
  }, [selectedTrip, aprobacionesPorReserva]);

  const todayLabel = useMemo(
    () =>
      startOfDay(new Date()).toLocaleDateString("es-MX", {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
    []
  );

  // 👇 Abrir modal de inicio — sólo si estado = 3
  function openStartModal() {
    if (hasSessionErrors) {
      setAlertDialog({
        open: true,
        title: "Error de Sesión",
        message: "No es posible iniciar viajes porque falta información crítica del usuario de sesión.",
        variant: "error"
      });
      return;
    }

    if (!selectedTrip) return;

    const estadoId = Number(selectedTrip.meta?.idEstadoReserva ?? 0);
    if (estadoId !== 3) {
      setAlertDialog({
        open: true,
        title: "Aviso",
        message: "Solo puedes iniciar viajes que estén en estado AUTORIZADA (3).",
        variant: "info"
      });
      return;
    }

    if (!selectedVehicleId) {
      const metaVeh = Number(selectedTrip.meta?.idVehiculo ?? 0);
      if (metaVeh > 0) {
        setSelectedVehicleId(String(metaVeh));
      }
    }

    setStartForm({
      combustibleInicial: "",
      kmInicial: "",
      observacionesInicial: "",
    });

    setShowStartModal(true);
  }

  // 👇 Abrir modal de fin — sólo si estado = 2
  function openFinishModal() {
    if (hasSessionErrors) {
      setAlertDialog({
        open: true,
        title: "Error de Sesión",
        message: "No es posible concluir viajes porque falta información crítica del usuario de sesión.",
        variant: "error"
      });
      return;
    }

    if (!selectedTrip) return;

    const estadoId = Number(selectedTrip.meta?.idEstadoReserva ?? 0);
    if (estadoId !== 2) {
      setAlertDialog({
        open: true,
        title: "Aviso",
        message: "Solo puedes concluir viajes que ya fueron iniciados (estado 2).",
        variant: "info"
      });
      return;
    }

    if (!selectedVehicleId) {
      const metaVeh = Number(selectedTrip.meta?.idVehiculo ?? 0);
      if (metaVeh > 0) {
        setSelectedVehicleId(String(metaVeh));
      }
    }

    setFinishForm({
      combustibleFinal: "",
      kmFinal: "",
      observacionesFinal: "",
      huboSiniestro: false,
      tipoSiniestro: "",
      importeSiniestro: "",
      descripcionSiniestro: "",
      recargoCombustible: false,
      litros: "",
      importeCombustible: "",
      estacion: "",
      notasCombustible: "",
    });

    setShowFinishModal(true);
  }

  // 👇 Confirmar inicio de viaje: sólo si estado actual = 3 → pasa a 2
  async function handleConfirmStart(e) {
    e.preventDefault();

    if (hasSessionErrors) {
      setAlertDialog({
        open: true,
        title: "Error de Sesión",
        message: "No es posible guardar el inicio del viaje porque falta información crítica del usuario de sesión.",
        variant: "error"
      });
      return;
    }

    if (!selectedTrip) return;

    const estadoId = Number(selectedTrip.meta?.idEstadoReserva ?? 0);
    if (estadoId !== 3) {
      setAlertDialog({
        open: true,
        title: "Aviso",
        message: "Solo puedes confirmar inicio de viajes AUTORIZADOS (estado 3).",
        variant: "info"
      });
      return;
    }

    const idVehiculoNum = selectedVehicleId ? Number(selectedVehicleId) : null;
    if (!idVehiculoNum) {
      setAlertDialog({
        open: true,
        title: "Falta Información",
        message: "Selecciona el vehículo con el que realizarás el viaje.",
        variant: "info"
      });
      return;
    }

    if (!startForm.kmInicial) {
      setAlertDialog({
        open: true,
        title: "Falta Información",
        message: "Captura el kilometraje del odómetro al inicio del viaje.",
        variant: "info"
      });
      return;
    }

    const kmInicialNum = Number(startForm.kmInicial);
    if (Number.isNaN(kmInicialNum) || kmInicialNum < 0) {
      setAlertDialog({
        open: true,
        title: "Dato Inválido",
        message: "El kilometraje inicial no es válido.",
        variant: "error"
      });
      return;
    }

    const veh = getCurrentVehicle();
    const vehDesc = veh ? `${veh.marca} ${veh.modelo}` : "";

    const m = selectedTrip.meta || {};
    const reservaId = Number(m.idReserva ?? selectedTrip.id);

    if (!Number.isInteger(reservaId) || reservaId <= 0) {
      console.error("Reserva sin IdReserva válido en INICIO:", m);
      setAlertDialog({
        open: true,
        title: "Error de Datos",
        message: "La reserva seleccionada no tiene un IdReserva válido.",
        variant: "error"
      });
      return;
    }

    const dtoUpdate = {
      idReserva: reservaId,
      idSolicitante: m.idSolicitante,
      idChofer: m.idChofer,
      idVehiculo: idVehiculoNum,
      fechaInicio: m.fechaInicio,
      fechaFin: m.fechaFin,
      horaInicio: m.horaInicio,
      horaFin: m.horaFin,
      origenTexto: m.origenTexto,
      destinoTexto: m.destinoTexto,
      motivo: m.motivo,
      idEstadoReserva: 2, // pasa a CONFIRMADA / en curso
      fueraHorarioMin: m.fueraHorarioMin,
      observaciones: m.observaciones,
      fechaCreacion: m.fechaCreacion,
      activo: m.activo,
      ...(m.idTipoAprobacion > 0 && { idTipoAprobacion: m.idTipoAprobacion })
    };

    try {
      await axios.put(endpointReservas, dtoUpdate);

      const checklistInicioDto = {
        idChecklist: 0,
        idReserva: reservaId,
        tipo: "RECEPCION",
        combustible: startForm.combustibleInicial || "",
        kmOdometro: kmInicialNum,
        observaciones: startForm.observacionesInicial || "",
        fechaRegistro: new Date().toISOString(),
        activo: true,
      };

      await axios.post(endpointChecklistViaje, checklistInicioDto);
    } catch (err) {
      console.error(
        "Error al guardar inicio de viaje (reserva/checklist)",
        err
      );
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudo guardar el inicio del viaje. Intenta de nuevo.",
        variant: "error"
      });
      return;
    }

    setTripNewStatus(reservaId, "EN_CURSO", {
      idVehiculo: idVehiculoNum,
      vehiculoDesc: vehDesc,
      idEstadoReserva: 2,
      viajeChecklistInicioAt: new Date().toISOString(),
    });

    setShowStartModal(false);
  }

  // 👇 Confirmar fin de viaje — sólo si estado actual = 2 → pasa a 6
  async function handleConfirmFinish(e) {
    e.preventDefault();

    if (hasSessionErrors) {
      setAlertDialog({
        open: true,
        title: "Error de Sesión",
        message: "No es posible guardar el cierre del viaje porque falta información crítica del usuario de sesión.",
        variant: "error"
      });
      return;
    }

    if (!selectedTrip) return;

    const estadoId = Number(selectedTrip.meta?.idEstadoReserva ?? 0);
    if (estadoId !== 2) {
      setAlertDialog({
        open: true,
        title: "Aviso",
        message: "Solo puedes concluir viajes que ya fueron iniciados (estado 2).",
        variant: "info"
      });
      return;
    }

    const idVehiculoNum = selectedVehicleId ? Number(selectedVehicleId) : null;
    if (!idVehiculoNum) {
      setAlertDialog({
        open: true,
        title: "Falta Información",
        message: "Falta el vehículo del viaje.",
        variant: "error"
      });
      return;
    }

    const kmFinalNum = finishForm.kmFinal ? Number(finishForm.kmFinal) : null;
    if (kmFinalNum == null || Number.isNaN(kmFinalNum) || kmFinalNum < 0) {
      setAlertDialog({
        open: true,
        title: "Dato Inválido",
        message: "Captura un kilometraje final válido.",
        variant: "error"
      });
      return;
    }

    if (finishForm.recargoCombustible) {
      if (!finishForm.litros || Number(finishForm.litros) <= 0) {
        setAlertDialog({
          open: true,
          title: "Falta Información",
          message: "Captura los litros cargados.",
          variant: "info"
        });
        return;
      }
      if (
        finishForm.importeCombustible === "" ||
        Number(finishForm.importeCombustible) < 0
      ) {
        setAlertDialog({
          open: true,
          title: "Dato Inválido",
          message: "Captura un importe de combustible válido (>= 0).",
          variant: "error"
        });
        return;
      }
    }

    if (finishForm.huboSiniestro) {
      if (!finishForm.tipoSiniestro.trim()) {
        setAlertDialog({
          open: true,
          title: "Falta Información",
          message: "Captura el tipo de siniestro.",
          variant: "info"
        });
        return;
      }
      if (!finishForm.descripcionSiniestro.trim()) {
        setAlertDialog({
          open: true,
          title: "Falta Información",
          message: "Captura la descripción del siniestro.",
          variant: "info"
        });
        return;
      }
      if (
        finishForm.importeSiniestro === "" ||
        Number(finishForm.importeSiniestro) < 0
      ) {
        setAlertDialog({
          open: true,
          title: "Dato Inválido",
          message: "Captura un importe de siniestro válido (>= 0).",
          variant: "error"
        });
        return;
      }
    }

    const m = selectedTrip.meta || {};
    const reservaId = Number(m.idReserva ?? selectedTrip.id);

    if (!Number.isInteger(reservaId) || reservaId <= 0) {
      console.error("Reserva sin IdReserva válido en FIN:", m);
      setAlertDialog({
        open: true,
        title: "Error de Datos",
        message: "La reserva seleccionada no tiene un IdReserva válido.",
        variant: "error"
      });
      return;
    }

    // Horas extra netas del viaje (solo fuera de horario / finde / festivo), en horas completas
    const extraHoursNetas = computeNetExtraHoursForTrip(selectedTrip);

    // ¿Esta reserva tuvo flujo de aprobación?
    const tipoAprobReserva = m.idTipoAprobacion ?? m.IdTipoAprobacion ?? null;

    const dtoUpdateFinish = {
      idReserva: reservaId,
      idSolicitante: m.idSolicitante,
      idChofer: m.idChofer,
      idVehiculo: idVehiculoNum,
      fechaInicio: m.fechaInicio,
      fechaFin: m.fechaFin,
      horaInicio: m.horaInicio,
      horaFin: m.horaFin,
      origenTexto: m.origenTexto,
      destinoTexto: m.destinoTexto,
      motivo: m.motivo,
      idEstadoReserva: 6, // COMPLETADA
      fueraHorarioMin: m.fueraHorarioMin,
      observaciones: m.observaciones,
      fechaCreacion: m.fechaCreacion,
      activo: m.activo,
      ...(m.idTipoAprobacion > 0 && { idTipoAprobacion: m.idTipoAprobacion })
    };

    try {
      await axios.put(endpointReservas, dtoUpdateFinish);

      const checklistFinDto = {
        idChecklist: 0,
        idReserva: reservaId,
        tipo: "ENTREGA",
        combustible: finishForm.combustibleFinal || "",
        kmOdometro: kmFinalNum,
        observaciones: finishForm.observacionesFinal || "",
        fechaRegistro: new Date().toISOString(),
        activo: true,
      };

      await axios.post(endpointChecklistViaje, checklistFinDto);

      if (finishForm.recargoCombustible && endpointCargasCombustible) {
        const cargaDto = {
          idCarga: 0,
          idVehiculo: idVehiculoNum,
          idReserva: reservaId,
          fechaCarga: new Date().toISOString(),
          litros: Number(finishForm.litros) || 0,
          importe: Number(finishForm.importeCombustible) || 0,
          kmOdometro: kmFinalNum,
          estacion: finishForm.estacion || "",
          notas: finishForm.notasCombustible || "",
          fechaCreacion: new Date().toISOString(),
          activo: true,
        };

        await axios.post(endpointCargasCombustible, cargaDto);
      }

      if (finishForm.huboSiniestro && endpointSiniestros) {
        const siniestroDto = {
          idSiniestro: 0,
          idVehiculo: idVehiculoNum,
          idReserva: reservaId,
          tipo: finishForm.tipoSiniestro || "",
          fecha: new Date().toISOString(),
          importe: Number(finishForm.importeSiniestro) || 0,
          descripcion: finishForm.descripcionSiniestro || "",
          fechaCreacion: new Date().toISOString(),
          activo: true,
        };

        await axios.post(endpointSiniestros, siniestroDto);
      }
    } catch (err) {
      console.error("Error al guardar cierre de viaje (BD)", err);
      setAlertDialog({
        open: true,
        title: "Error",
        message: "No se pudo guardar el cierre del viaje en la base de datos. Intenta de nuevo.",
        variant: "error"
      });
      return;
    }

    setTripNewStatus(reservaId, "COMPLETADO", {
      viajeChecklistFinAt: new Date().toISOString(),
      idEstadoReserva: 6,
    });

    setShowFinishModal(false);
  }

  return (
    <div className="page driver-page">
      <header className="driver-card ">
        <div className="reservation-section__title">
          AutoReserva · Chofer
          <br />
          Bienvenido, {driverName}
        </div>
      </header>

      <main className="driver-layout">
        {hasSessionErrors ? (
          // 🔒 Bloqueo total de la ventana si faltan datos críticos
          // 🔒 Bloqueo total de la ventana si faltan datos críticos
          <div style={{
            gridColumn: '1 / -1',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            minHeight: '60vh', padding: '2rem'
          }}>
            <div className="card" style={{
              maxWidth: '600px', width: '100%',
              padding: '2.5rem',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              backgroundColor: '#fff',
              textAlign: 'center',
              borderTop: '5px solid #ef4444'
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                backgroundColor: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.5rem auto', fontSize: '32px'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <h2 style={{
                fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937',
                marginBottom: '1rem'
              }}>
                Acceso Restringido
              </h2>
              <p style={{
                color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.6'
              }}>
                Lo sentimos, no podemos cargar el panel de chofer porque tu usuario no cuenta con la configuración necesaria.
              </p>

              <div style={{
                textAlign: 'left', backgroundColor: '#f9fafb',
                padding: '1rem 1.5rem', borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <p style={{
                  fontSize: '0.875rem', fontWeight: '600', color: '#374151',
                  marginTop: 0, marginBottom: '0.5rem'
                }}>
                  Detalles del error:
                </p>
                <ul style={{
                  margin: 0, paddingLeft: '1.25rem',
                  fontSize: '0.875rem', color: '#ef4444'
                }}>
                  {sessionErrors.map((msg, idx) => (
                    <li key={idx} style={{ marginBottom: '0.25rem' }}>{msg}</li>
                  ))}
                </ul>
              </div>

              <div style={{ marginTop: '2rem' }}>
                <button
                  className="btn-action"
                  onClick={() => window.location.reload()}
                >
                  Intentar recargar
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* IZQUIERDA: calendario + lista */}
            <section className="card driver-left">
              <div className="driver-left-head">
                <div>
                  <h2>Mis viajes de la semana</h2>
                  <p className="driver-sub">
                    Hoy: <strong>{todayLabel}</strong>
                  </p>
                </div>
              </div>

              <WeeklyCalendar
                anchorDate={anchor}
                onNavigate={(newAnchor) => setAnchor(newAnchor)}
                events={tripsForWeek.map((evt) => ({
                  ...evt,
                  meta: {
                    ...evt.meta,
                    calendarGradient: getCalendarEventGradient(evt.meta),
                  },
                }))}
                availabilityBlocks={availabilityBlocks}
                onEventClick={handleEventClick}
              />

              <div className="driver-trip-list">
                <h3>Próximos viajes autorizados</h3>
                {tripsForWeek.length === 0 && (
                  <div className="driver-empty">
                    No tienes viajes autorizados por ahora.
                  </div>
                )}
                {tripsForWeek.map((t) => {
                  const st = (t.meta?.viajeStatus || "PENDIENTE").toUpperCase();
                  return (
                    <button
                      key={t.id}
                      className={
                        "driver-trip-item" +
                        (selectedTrip && selectedTrip.id === t.id
                          ? " active"
                          : "")
                      }
                      onClick={() => handleEventClick(t)}
                    >
                      <div className="driver-trip-title">
                        {t.meta?.originParsed?.address || "Origen"} →{" "}
                        {t.meta?.destinationParsed?.address || "Destino"}
                      </div>
                      <div className="driver-trip-meta">
                        {formatDateTimeRange(t.start, t.end)}{" · "}
                        <span
                          className={
                            "driver-badge status-" +
                            st.toLowerCase().replace(/_/g, "-")
                          }
                        >
                          {st.replace(/_/g, " ")}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* DERECHA: detalle del viaje */}
            <section className="card driver-right">
              {!selectedTrip ? (
                <div className="driver-detail-empty">
                  Selecciona un viaje en el calendario o en la lista.
                </div>
              ) : (
                <>
                  <div className="driver-detail-head">
                    <div className="reservation-section__title">
                      <h3>Detalle del viaje</h3>
                    </div>
                  </div>
                  <div className="driver-detail-head">

                    <div>
                      <label className="driver-item-title form-group">
                        Pasajero:{" "}
                        <strong>
                          {selectedTrip.meta?.nombre || "N/D"}
                        </strong>
                      </label>
                    </div>
                    <span
                      className={
                        "driver-badge status-" +
                        selectedStatus.toLowerCase().replace(/_/g, "-")
                      }
                    >
                      {selectedStatus.replace(/_/g, " ")}
                    </span>
                  </div>

                  <div className="driver-grid">
                    <div>
                      <label className="driver-item-title form-group">Horario</label>
                      <p className="driver-value">
                        {formatDateTimeRange(selectedTrip.start, selectedTrip.end)}
                      </p>
                    </div>
                    <div>
                      <label className="driver-item-title form-group">Origen</label>
                      <p className="driver-value">
                        {selectedTrip.meta?.originParsed?.address ||
                          "No capturado"}
                      </p>
                    </div>
                    <div>
                      <label className="driver-item-title form-group">Destino</label>
                      <p className="driver-value">
                        {selectedTrip.meta?.destinationParsed?.address ||
                          "No capturado"}
                      </p>
                    </div>
                    <div>
                      <label className="driver-item-title form-group">Motivo</label>
                      <p className="driver-value">
                        {selectedTrip.meta?.motivo || "Sin motivo"}
                      </p>
                    </div>
                    <div>
                      <label className="driver-item-title form-group">Observaciones</label>
                      <p className="driver-value">
                        {selectedTrip.meta?.observaciones ||
                          "Sin observaciones"}
                      </p>
                    </div>
                    <div>
                      <label className="driver-item-title form-group">
                        Comentario de RH
                      </label>
                      <p className="driver-value">
                        {selectedRhComment.trim() ||
                          "Sin comentario especial de RH"}
                      </p>
                    </div>
                  </div>

                  <div className="driver-vehicle-block">
                    <label className="driver-item-title form-group">
                      Vehículo para este viaje
                    </label>
                    <select
                      className="driver-select"
                      value={selectedVehicleId}
                      onChange={(e) =>
                        setSelectedVehicleId(e.target.value)
                      }
                    >
                      <option value="">Selecciona un vehículo</option>
                      {sortedVehicles.map((v) => {
                        const isPreferred = preferredVehicleSet.has(Number(v.idVehiculo));
                        const baseLabel = `${v.marca || ""} ${v.modelo || ""}`.trim();
                        return (
                          <option
                            key={v.idVehiculo}
                            value={v.idVehiculo}
                            data-preferred={isPreferred ? "yes" : "no"}
                          >
                            {isPreferred ? "★ " : ""}
                            {baseLabel || "Vehículo"} · {v.placa}
                          </option>
                        );
                      })}
                    </select>
                    {selectedVehicleId && (
                      <div className="driver-vehicle-summary">
                        {(() => {
                          const veh = vehicles.find(
                            (v) =>
                              String(v.idVehiculo) === selectedVehicleId
                          );
                          if (!veh) return null;
                          return (
                            <>
                              <label className="driver-item-title form-group">
                                Tipo:{" "}
                                {veh.tipoDescripcion ||
                                  veh.idTipoVehiculo}{" "}
                                · Capacidad: {veh.capacidad} pasajeros
                              </label>
                              <label className="driver-item-title form-group">
                                Económico: {veh.economico} · Color:{" "}
                                {veh.color}
                              </label>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="driver-map-block">
                    <TripMapViewer
                      origin={selectedTrip.meta?.originParsed}
                      destination={selectedTrip.meta?.destinationParsed}
                    />
                  </div>

                  <div className="driver-actions">
                    <button
                      className="btn"
                      type="button"
                      disabled={!canStart || !selectedVehicleId}
                      onClick={openStartModal}
                    >
                      Confirmar inicio de viaje
                    </button>
                    <button
                      className="btn primary"
                      type="button"
                      disabled={!canFinish}
                      onClick={openFinishModal}
                    >
                      Marcar viaje como concluido
                    </button>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </main>

      {/* MODAL: inicio */}
      {showStartModal && !hasSessionErrors && (
        <div className="driver-modal-overlay" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div className="driver-modal" style={{
            background: 'white', borderRadius: '12px', width: 'min(90%, 500px)',
            maxHeight: '600px', overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)', position: 'relative',
            display: 'block'
          }}>
            <h3>Checklist de inicio de viaje</h3>
            <form
              onSubmit={handleConfirmStart}
              className="driver-modal-form"
            >
              <label>
                Combustible actual
                <input
                  value={startForm.combustibleInicial}
                  onChange={(e) =>
                    setStartForm((f) => ({
                      ...f,
                      combustibleInicial: e.target.value,
                    }))
                  }
                  placeholder="Ej. 40 litros"
                />
              </label>
              <label>
                Km odómetro (inicio)
                <input
                  type="number"
                  value={startForm.kmInicial}
                  onChange={(e) =>
                    setStartForm((f) => ({
                      ...f,
                      kmInicial: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Observaciones
                <textarea
                  rows={3}
                  value={startForm.observacionesInicial}
                  onChange={(e) =>
                    setStartForm((f) => ({
                      ...f,
                      observacionesInicial: e.target.value,
                    }))
                  }
                />
              </label>

              <div className="driver-modal-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowStartModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn primary">
                  Guardar inicio
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: fin */}
      {showFinishModal && !hasSessionErrors && (
        <div className="driver-modal-overlay" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div className="driver-modal" style={{
            background: 'white', borderRadius: '12px', width: 'min(90%, 500px)',
            maxHeight: '600px', overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)', position: 'relative',
            display: 'block'
          }}>
            <h3>Cierre de viaje</h3>
            <form
              onSubmit={handleConfirmFinish}
              className="driver-modal-form"
            >
              <label>
                Km odómetro (final)
                <input
                  type="number"
                  value={finishForm.kmFinal}
                  onChange={(e) =>
                    setFinishForm((f) => ({
                      ...f,
                      kmFinal: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Combustible al finalizar
                <input
                  value={finishForm.combustibleFinal}
                  onChange={(e) =>
                    setFinishForm((f) => ({
                      ...f,
                      combustibleFinal: e.target.value,
                    }))
                  }
                  placeholder="Ej. 1/2 tanque"
                />
              </label>
              <label>
                Observaciones
                <textarea
                  rows={3}
                  value={finishForm.observacionesFinal}
                  onChange={(e) =>
                    setFinishForm((f) => ({
                      ...f,
                      observacionesFinal: e.target.value,
                    }))
                  }
                />
              </label>

              <hr className="driver-modal-sep" />

              <div
                onClick={() => setFinishForm(f => ({ ...f, recargoCombustible: !f.recargoCombustible }))}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', padding: '1rem 0',
                  borderBottom: '1px solid #f1f5f9',
                  userSelect: 'none', transition: 'all 0.2s',
                  color: finishForm.recargoCombustible ? '#0f172a' : '#64748b'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 500 }}>
                  <span>⛽</span> Registrar Carga de Combustible
                </div>
                <div style={{
                  transform: finishForm.recargoCombustible ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease', fontSize: '0.75rem', opacity: 0.6
                }}>
                  ▼
                </div>
              </div>

              <div style={{
                maxHeight: finishForm.recargoCombustible ? '500px' : '0',
                opacity: finishForm.recargoCombustible ? 1 : 0,
                overflow: 'hidden',
                transition: 'all 0.4s ease-in-out'
              }}>
                <div className="driver-modal-grid" style={{ padding: '1rem 0' }}>
                  <label>
                    Litros
                    <input
                      type="number"
                      value={finishForm.litros}
                      onChange={(e) =>
                        setFinishForm((f) => ({
                          ...f,
                          litros: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    Importe
                    <input
                      type="number"
                      value={finishForm.importeCombustible}
                      onChange={(e) =>
                        setFinishForm((f) => ({
                          ...f,
                          importeCombustible: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    Estación
                    <input
                      value={finishForm.estacion}
                      onChange={(e) =>
                        setFinishForm((f) => ({
                          ...f,
                          estacion: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    Notas
                    <input
                      value={finishForm.notasCombustible}
                      onChange={(e) =>
                        setFinishForm((f) => ({
                          ...f,
                          notasCombustible: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>

              <hr className="driver-modal-sep" />

              <div
                onClick={() => setFinishForm(f => ({ ...f, huboSiniestro: !f.huboSiniestro }))}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', padding: '1rem 0',
                  borderBottom: '1px solid #f1f5f9',
                  userSelect: 'none', transition: 'all 0.2s',
                  color: finishForm.huboSiniestro ? '#0f172a' : '#64748b'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 500 }}>
                  <span>⚠️</span> Reportar Siniestro / Incidente
                </div>
                <div style={{
                  transform: finishForm.huboSiniestro ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease', fontSize: '0.75rem', opacity: 0.6
                }}>
                  ▼
                </div>
              </div>

              <div style={{
                maxHeight: finishForm.huboSiniestro ? '500px' : '0',
                opacity: finishForm.huboSiniestro ? 1 : 0,
                overflow: 'hidden',
                transition: 'all 0.4s ease-in-out'
              }}>
                <div className="driver-modal-grid" style={{ padding: '1rem 0' }}>
                  <label>
                    Tipo de siniestro
                    <select
                      className="driver-select"
                      value={finishForm.tipoSiniestro}
                      onChange={(e) =>
                        setFinishForm((f) => ({
                          ...f,
                          tipoSiniestro: e.target.value,
                        }))
                      }
                    >
                      <option value="">Selecciona tipo</option>
                      <option value="CHOQUE">CHOQUE</option>
                      <option value="ROBO">ROBO</option>
                      <option value="DANIO">DAÑO</option>
                      <option value="MULTA">MULTA</option>
                      <option value="OTRO">OTRO</option>
                    </select>
                  </label>
                  <label>
                    Importe estimado
                    <input
                      type="number"
                      value={finishForm.importeSiniestro}
                      onChange={(e) =>
                        setFinishForm((f) => ({
                          ...f,
                          importeSiniestro: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label style={{ gridColumn: '1 / -1' }}>
                    Descripción
                    <textarea
                      rows={2}
                      value={finishForm.descripcionSiniestro}
                      onChange={(e) =>
                        setFinishForm((f) => ({
                          ...f,
                          descripcionSiniestro: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>

              <div className="driver-modal-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowFinishModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn primary">
                  Guardar cierre
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AlertDialog
        open={alertDialog.open}
        title={alertDialog.title}
        message={alertDialog.message}
        variant={alertDialog.variant}
        onClose={() => {
          setAlertDialog({ ...alertDialog, open: false });
          if (alertDialog.onConfirm) alertDialog.onConfirm();
        }}
        buttonText="Aceptar"
      />
    </div >
  );
}
